import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';

import { AnalyticsService } from '../../core/services/analytics.service';
import { EventDetailsParser, filledCount, loadLocalParser, ParseResult } from './event-details-parser';
import { AudioCaptureService } from './audio-capture.service';
import { CaptureFailure, CaptureSession } from './capture-types';

export type VoiceState = 'idle' | 'listening' | 'typing' | 'thinking' | 'result' | 'error';
export type VoiceFailure = CaptureFailure | 'not_understood' | 'parser_load_failed';

export interface VoiceParsed {
  result: ParseResult;
  text: string;
  input: 'voice' | 'typed';
  parseMs: number;
}

/**
 * "Say it instead" (plan §7): captures a sentence by voice or typing, parses
 * it in the browser, and hands the result to the form, which decides what may
 * change (voice-merge.ts) and passes back the notes to show.
 *
 * Voice = a recorded clip sent to pwa-transcribe-voice (AudioCaptureService);
 * without that endpoint or a recorder, the panel offers typing only. The
 * transcript never goes to PostHog, and the clip is not stored.
 */
@Component({
  selector: 'app-voice-panel',
  templateUrl: './voice-panel.component.html',
  styleUrls: ['./voice-panel.component.scss'],
})
export class VoicePanelComponent implements OnInit, OnDestroy {
  @Input() surface = 'sheet';
  @Input() maxSeconds = 30;
  /** Popular + the host's own, for category matching. */
  @Input() categories: string[] = [];
  /** Guesses the form made from this parse, to show under "We heard". */
  @Input() notes: string[] = [];
  /** "We couldn't catch the start time. Pick it below." — or ''. */
  @Input() partialMessage = '';
  /** The host has edited the form since: shrink to one line. */
  @Input() collapsed = false;

  @Output() parsed = new EventEmitter<VoiceParsed>();
  @Output() undo = new EventEmitter<void>();
  /** First use of voice or typing — the form loads the host's own categories then. */
  @Output() engaged = new EventEmitter<void>();

  state: VoiceState = 'idle';
  failure: VoiceFailure | null = null;
  canSpeak = false;
  /** The idle tile's resting waveform: bar heights in %, decoration only. */
  readonly wave = [30, 45, 35, 25, 20, 20, 25, 20, 20, 25, 35, 45, 40, 30, 25, 20, 25, 20, 20, 30, 45, 55, 40, 30,
    25, 20, 20, 25, 20, 25, 35, 30, 25, 20, 25, 30, 45, 60, 45, 70];
  transcript = '';
  typed = '';
  elapsed = 0;

  private session: CaptureSession | null = null;
  private sub: Subscription | null = null;
  private timer: any = null;
  private parser: Promise<EventDetailsParser> | null = null;
  private lastInput: 'voice' | 'typed' = 'voice';
  private permissionLogged = false;

  constructor(private recorder: AudioCaptureService, private analytics: AnalyticsService) {}

  ngOnInit(): void {
    this.canSpeak = this.recorder.isSupported();
    this.analytics.capture('create_event_voice_shown', { surface: this.surface, mode: this.canSpeak ? 'voice' : 'type', engine: this.engine });
  }

  /** Kept in analytics so events stay comparable with the old browser-engine data. */
  private get engine(): string {
    return this.canSpeak ? 'recorder' : 'none';
  }

  ngOnDestroy(): void {
    this.stopCapture(true);
  }

  get secondsLeft(): number {
    return Math.max(0, this.maxSeconds - this.elapsed);
  }

  get timerLabel(): string {
    const pad = (n: number) => `0:${n.toString().padStart(2, '0')}`;
    return `${pad(Math.min(this.elapsed, this.maxSeconds))} / ${pad(this.maxSeconds)}`;
  }

  // ── voice ──────────────────────────────────────────────────────────────

  listen(retry = false): void {
    if (!this.canSpeak) {
      this.type();
      return;
    }
    this.stopCapture(true);
    this.engaged.emit();
    this.lastInput = 'voice';
    this.failure = null;
    this.transcript = '';
    this.elapsed = 0;
    this.state = 'listening';
    this.preloadParser();
    this.analytics.capture(retry ? 'create_event_voice_retried' : 'create_event_voice_started', { surface: this.surface, input: 'voice', engine: this.engine });

    this.session = this.recorder.start({ lang: navigator.language || 'en-IN', maxSeconds: this.maxSeconds, silenceMs: 2500 });
    this.sub = this.session.events$.subscribe(e => {
      switch (e.type) {
        case 'started':
          this.logPermission('granted');
          this.timer = setInterval(() => this.elapsed++, 1000);
          break;
        case 'transcribing':
          // The clip is recorded; the words come back from the server.
          this.clearTimer();
          this.state = 'thinking';
          break;
        case 'final':
          this.clearTimer();
          this.transcript = e.text;
          this.analytics.capture('create_event_voice_captured', {
            surface: this.surface, seconds: e.seconds, chars: e.text.length, ended_by: e.endedBy, engine: this.engine,
          });
          this.parse(e.text, 'voice');
          break;
        case 'error':
          this.clearTimer();
          if (e.reason === 'aborted') return;
          if (e.reason === 'mic_blocked') this.logPermission('denied');
          this.fail(e.reason);
          break;
      }
    });
  }

  /** Done while listening: keep what was heard. */
  finish(): void {
    this.session?.stop();
  }

  cancel(): void {
    this.stopCapture(true);
    this.analytics.capture('create_event_voice_failed', { surface: this.surface, reason: 'aborted' });
    this.state = 'idle';
  }

  // ── typing ─────────────────────────────────────────────────────────────

  type(): void {
    this.stopCapture(true);
    this.engaged.emit();
    this.lastInput = 'typed';
    this.failure = null;
    this.state = 'typing';
    this.preloadParser();
  }

  submitTyped(): void {
    const text = this.typed.trim();
    if (!text) return;
    this.analytics.capture('create_event_voice_started', { surface: this.surface, input: 'typed' });
    this.transcript = text;
    this.parse(text, 'typed');
  }

  // ── result ─────────────────────────────────────────────────────────────

  again(): void {
    if (this.lastInput === 'typed' || !this.canSpeak) {
      this.analytics.capture('create_event_voice_retried', { surface: this.surface, input: 'typed' });
      this.type();
    } else {
      this.listen(true);
    }
  }

  onUndo(): void {
    this.state = 'idle';
    this.transcript = '';
    this.undo.emit();
  }

  /** Called by the form after Undo from elsewhere, or when it resets. */
  reset(): void {
    this.stopCapture(true);
    this.state = 'idle';
    this.transcript = '';
    this.failure = null;
  }

  // ── internals ──────────────────────────────────────────────────────────

  private preloadParser(): void {
    if (!this.parser) {
      this.parser = loadLocalParser();
      // A failed chunk load must be retryable next time.
      this.parser.catch(() => (this.parser = null));
    }
  }

  private async parse(text: string, input: 'voice' | 'typed'): Promise<void> {
    this.state = 'thinking';
    this.preloadParser();
    let parser: EventDetailsParser;
    try {
      parser = await this.parser!;
    } catch {
      this.fail(navigator.onLine === false ? 'offline' : 'parser_load_failed');
      return;
    }
    const t0 = performance.now();
    const result = parser.parse(text, {
      now: DateTime.now(),
      locale: navigator.language || 'en-IN',
      categories: this.categories,
    });
    const parseMs = Math.round(performance.now() - t0);
    if (filledCount(result) === 0) {
      this.fail('not_understood');
      return;
    }
    this.state = 'result';
    this.parsed.emit({ result, text, input, parseMs });
  }

  private fail(reason: VoiceFailure): void {
    this.failure = reason;
    this.state = 'error';
    this.analytics.capture('create_event_voice_failed', { surface: this.surface, reason, engine: this.engine });
  }

  private logPermission(result: 'granted' | 'denied'): void {
    if (this.permissionLogged) return;
    this.permissionLogged = true;
    this.analytics.capture('create_event_voice_permission', { surface: this.surface, result });
  }

  private clearTimer(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  private stopCapture(discard: boolean): void {
    this.clearTimer();
    if (discard) this.session?.abort();
    this.sub?.unsubscribe();
    this.sub = null;
    this.session = null;
  }
}
